"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { PlusCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline"
import { useLocale } from "@/context/LocaleContext"
import { faqData } from "@/data/landingPage/NewFAQData";
import { slideVariants, staggerContainerVariants, staggerItemVariants } from "@/utils/animation";

export default function NewFAQ() {
  const { t } = useLocale();
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="bg-gray-50 py-16 md:py-24 font-sans">
      <div className="max-w-7xl mx-auto px-5 md:px-8 xl:px-0">
        <motion.div
          variants={slideVariants(false, 0.6)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-10 md:mb-14"
        >
          <h2 className="text-[2rem] md:text-[2.6rem] font-bold text-[#2A2A2A] mb-3 tracking-tight">
            {t("LandingPage.faq_title") || "Pertanyaan Yang Sering Ditanyakan"}
          </h2>
          <p className="text-[1.05rem] md:text-[1.15rem] text-[#2A2A2A]">
            {t("LandingPage.faq_subtitle") || "Semua yang perlu kamu ketahui sebelum menggunakan Elynk.co."}
          </p>
        </motion.div>
        <motion.div
          variants={staggerContainerVariants(0.08)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-[900px] mx-auto flex flex-col space-y-3.5"
        >
          {faqData.map((faq, index) => {
            const isOpen = openIndex === index
            const translatedQuestion = t(`LandingPage.faq_q_${index}`) || faq.question;
            const translatedAnswer = t(`LandingPage.faq_a_${index}`) || faq.answer;

            return (
              <motion.div
                key={index}
                variants={staggerItemVariants(0.5)}
                className="bg-white rounded-[16px] md:rounded-[20px] shadow-[0_2px_15px_rgba(0,0,0,0.02)] border border-gray-100 overflow-hidden transition-colors duration-300"
              >
                {/* Accordion Header (Tombol Klik) */}
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex items-center justify-between p-5 md:py-6 md:px-8 text-left transition-colors hover:bg-gray-50/50"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-[1.05rem] md:text-[1.15rem] text-[#2A2A2A] pr-4">
                    {translatedQuestion}
                  </span>

                  {/* Icon Switcher (Plus / Minus) */}
                  <div className="shrink-0">
                    {isOpen ? (
                      <MinusCircleIcon className="w-7 h-7 text-[#E85C57]" strokeWidth={1.5} />
                    ) : (
                      <PlusCircleIcon className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
                    )}
                  </div>
                </button>

                {/* Accordion Body (Jawaban) */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                >
                  <div className="overflow-hidden">
                    <div
                      className="pb-6 px-5 md:px-8 pt-0 text-[1rem] md:text-[1.05rem] text-[#2A2A2A] leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: translatedAnswer }}
                    />
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

      </div>
    </section>
  )
}